import type { ExperienceMode, MediaActionApprovalRequest, WebActionApprovalRequest } from '@localagent/sdk';
import { namedTarget, NO_NAMED_TARGET } from './named-target';

export type ApprovalActionKind = 'web_action' | 'media_action';

interface WebActionApprovalCardProps {
  request: WebActionApprovalRequest | MediaActionApprovalRequest;
  onApprove: () => void | Promise<void>;
  onDeny: () => void | Promise<void>;
  isSubmitting?: boolean;
  error?: string | null;
  mode?: ExperienceMode;
  kind?: ApprovalActionKind;
}

function mediaNormalCopy(toolName: string): string {
  if (toolName === 'media_empty_trash') {
    return 'The agent wants to empty Trash';
  }
  return 'The agent wants to move this artifact to Trash';
}

export function WebActionApprovalCard({
  request,
  onApprove,
  onDeny,
  isSubmitting,
  error,
  mode,
  kind = 'web_action',
}: WebActionApprovalCardProps) {
  const isMedia = kind === 'media_action';
  const testid = isMedia ? 'media-action-approval' : 'web-action-approval';
  const isNormal = mode !== 'advanced';
  const target = namedTarget(request.arguments);
  const args = JSON.stringify(request.arguments, null, 2);
  const normalLead = isMedia
    ? mediaNormalCopy(request.toolName)
    : 'After reading the web, the agent wants to continue';

  return (
    <div
      data-testid={`${testid}-card`}
      data-approval-kind={kind}
      className="rounded bg-amber-500/10 p-2.5 dark:bg-amber-950/30"
    >
      {isNormal ? (
        <p className="mb-1 text-xs font-medium text-foreground">
          {normalLead}
          {target ? (
            <>
              {isMedia ? ': ' : ' with '}
              <span data-testid={`${testid}-target`}>{target}</span>
            </>
          ) : (
            <>
              . <span data-testid={`${testid}-no-target`}>{NO_NAMED_TARGET}</span>
            </>
          )}
        </p>
      ) : (
        <>
          <p className="mb-1 text-xs font-medium text-foreground">
            {isMedia ? 'The agent wants to run' : 'After reading the web, the agent wants to run'}{' '}
            <code
              data-testid={`${testid}-tool`}
              className="rounded bg-muted px-1 py-0.5 text-[11px]"
            >
              {request.toolName}
            </code>
          </p>
          <pre
            data-testid={`${testid}-arguments`}
            className="mb-2 max-h-40 overflow-auto rounded bg-card px-2 py-1.5 text-[11px] text-foreground"
          >
            {args}
          </pre>
        </>
      )}
      {error && (
        <p data-testid={`${testid}-error`} className="mb-2 text-[11px] text-destructive">
          {error}
        </p>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid={`${testid}-approve`}
          disabled={isSubmitting}
          className="rounded bg-amber-600 px-3 py-1 text-[11px] font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            void onApprove();
          }}
        >
          {isSubmitting ? 'Submitting…' : 'Approve'}
        </button>
        <button
          type="button"
          data-testid={`${testid}-deny`}
          disabled={isSubmitting}
          className="rounded px-3 py-1 text-[11px] text-muted-foreground hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => {
            void onDeny();
          }}
        >
          Deny
        </button>
      </div>
    </div>
  );
}
